// =========================================================================
// Org-mode aggregate services.
//
// Backs the web app's "Enter organization" shell with cross-store
// dashboards and combined Clients / Orders pivots. All RPCs gated server-
// side on `core.user_has_org_access` -- the org-mode session has no
// activeStoreId, so the safety is purely RPC-side, not RLS-side.
//
// Schema migration: supabase/migrations/20260614000000_org_mode_aggregates.sql.
// =========================================================================

import { supabase } from '../client';
import { requireOrgContext, type ActiveContext } from '../activeContext';
import {
  applyKeyset,
  buildNextCursor,
  clampPageSize,
  selectCountOption,
  type KeysetInput,
  type ListResult,
} from '../pagination';

// ---- types --------------------------------------------------------------

export interface OrgSalesSummaryRow {
  store_id: string | null;
  store_name: string;
  order_count: number;
  revenue_cents: number;
}

export interface OrgOrdersPipelineRow {
  store_id: string;
  store_name: string;
  status: string;
  order_count: number;
}

export interface OrgTopClientRow {
  client_id: string;
  display_name: string;
  store_id: string;
  store_name: string;
  order_count: number;
  revenue_cents: number;
}

export interface OrgClientRow {
  id: string;
  org_id: string;
  store_id: string;
  store_name: string;
  display_name: string;
  email: string | null;
  phone: string | null;
  status: 'active' | 'paused' | 'archived';
  created_at: string;
  updated_at: string;
}

export interface OrgOrderRow {
  id: string;
  org_id: string;
  store_id: string;
  store_name: string;
  client_id: string;
  client_display_name: string | null;
  number: string;
  status: string;
  total_cents: number;
  amount_paid_cents: number;
  currency: string;
  created_at: string;
  updated_at: string;
}

export interface OrgSalesRangeInput {
  from?: string | null;
  to?: string | null;
}

export interface OrgTopClientsInput extends OrgSalesRangeInput {
  limit?: number;
}

export interface OrgListClientsInput extends KeysetInput {
  search?: string;
}

export interface OrgListOrdersInput extends KeysetInput {
  status?: string[];
  search?: string;
}

// ---- helpers ------------------------------------------------------------

function client() {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return supabase() as any;
}

// =========================================================================
// 1. Sales summary
// =========================================================================

export async function getOrgSalesSummary(
  ctx: ActiveContext,
  input: OrgSalesRangeInput = {},
): Promise<OrgSalesSummaryRow[]> {
  const { orgId } = requireOrgContext(ctx);
  const { data, error } = await client()
    .schema('core')
    .rpc('rpc_org_sales_summary', {
      p_org_id: orgId,
      p_from: input.from ?? null,
      p_to: input.to ?? null,
    });
  if (error) throw error;
  return (data ?? []) as OrgSalesSummaryRow[];
}

// =========================================================================
// 2. Orders pipeline
// =========================================================================

export async function getOrgOrdersPipeline(
  ctx: ActiveContext,
): Promise<OrgOrdersPipelineRow[]> {
  const { orgId } = requireOrgContext(ctx);
  const { data, error } = await client()
    .schema('core')
    .rpc('rpc_org_orders_pipeline', { p_org_id: orgId });
  if (error) throw error;
  return (data ?? []) as OrgOrdersPipelineRow[];
}

// =========================================================================
// 3. Top clients
// =========================================================================

export async function getOrgTopClients(
  ctx: ActiveContext,
  input: OrgTopClientsInput = {},
): Promise<OrgTopClientRow[]> {
  const { orgId } = requireOrgContext(ctx);
  const { data, error } = await client()
    .schema('core')
    .rpc('rpc_org_top_clients', {
      p_org_id: orgId,
      p_from: input.from ?? null,
      p_to: input.to ?? null,
      p_limit: input.limit ?? 10,
    });
  if (error) throw error;
  return (data ?? []) as OrgTopClientRow[];
}

// =========================================================================
// 4. Combined clients list (keyset paginated)
// =========================================================================

export async function listOrgClients(
  ctx: ActiveContext,
  input: OrgListClientsInput = {},
): Promise<ListResult<OrgClientRow>> {
  const { orgId } = requireOrgContext(ctx);
  const pageSize = clampPageSize(input.pageSize);

  const { data, error } = await client()
    .schema('clients')
    .rpc('rpc_org_list_clients', {
      p_org_id: orgId,
      p_cursor: input.cursor ?? null,
      p_page_size: pageSize,
      p_search: input.search ?? null,
    });
  if (error) throw error;

  const rows = (data ?? []) as OrgClientRow[];
  return {
    rows,
    nextCursor: buildNextCursor(
      rows as unknown as Array<Record<string, unknown>>,
      pageSize,
      'updated_at',
    ),
    // RPCs don't return a count; org list views surface "Showing N+" instead.
    total: null,
    totalIsExact: false,
  };
}

// =========================================================================
// 5. Combined orders list (keyset paginated)
// =========================================================================

export async function listOrgOrders(
  ctx: ActiveContext,
  input: OrgListOrdersInput = {},
): Promise<ListResult<OrgOrderRow>> {
  const { orgId } = requireOrgContext(ctx);
  const pageSize = clampPageSize(input.pageSize);

  const { data, error } = await client()
    .schema('orders')
    .rpc('rpc_org_list_orders', {
      p_org_id: orgId,
      p_cursor: input.cursor ?? null,
      p_page_size: pageSize,
      p_status:
        input.status && input.status.length > 0 ? input.status : null,
      p_search: input.search ?? null,
    });
  if (error) throw error;

  const rows = (data ?? []) as OrgOrderRow[];
  return {
    rows,
    nextCursor: buildNextCursor(
      rows as unknown as Array<Record<string, unknown>>,
      pageSize,
      'created_at',
    ),
    total: null,
    totalIsExact: false,
  };
}

// Suppress unused import warning when only types are consumed.
export type { KeysetInput, ListResult, ActiveContext };
// applyKeyset is imported for future expansion (e.g., view-only org lists);
// kept here to keep the module's intent obvious to humans reading the file.
void applyKeyset;
void selectCountOption;
